// src/config/characters.ts
// Static character config for the gRoast live <RoastPlayer>.
// VERIFIED against the render-library worktree:
//   - remotion/Composition.tsx  (artboard/state-machine/view-model names + mouth ranges)
//   - src/components/rive/RiveRemotion.tsx:280-282 (ViewModel property names)
//
// ViewModel property contract (high-level @rive-app/react-webgl2 ViewModelInstance):
//   instance.enum("Emotions")  -> "Idle" | "Talking" | "Surprised" | "Point"
//   instance.number("Mouth")   -> live mouth amplitude (driven by audio RMS)
//   instance.boolean("Censor") -> true while a bleeped/censored word is active

export type CharacterId = "roast" | "toast";
export type Emotion = "Idle" | "Talking" | "Surprised" | "Point";

export interface CharacterConfig {
  id: CharacterId;
  label: string;
  riveSource: `/characters/${string}.riv`;
  artboardName: string;
  stateMachineName: string;
  viewModelName: string;
  emotionInputName: "Emotions";
  emotions: readonly Emotion[];
  mouthInputName: "Mouth";
  mouthClosedValue: number;
  mouthOpenMinValue: number;
  mouthOpenMaxValue: number;
  censorInputName: "Censor";
}

export const CHARACTERS: Record<CharacterId, CharacterConfig> = {
  roast: {
    id: "roast",
    label: "Mr. Roast",
    riveSource: "/characters/mr_roast.riv",
    artboardName: "mrroast",          // Composition.tsx:657
    stateMachineName: "SM_Main",      // Composition.tsx:658
    viewModelName: "MrRoast",         // Composition.tsx:659
    emotionInputName: "Emotions",     // RiveRemotion.tsx:280
    emotions: ["Idle", "Talking", "Surprised", "Point"] as const,
    mouthInputName: "Mouth",          // RiveRemotion.tsx:281
    mouthClosedValue: 0,              // Composition.tsx:1273 (mode !== toast)
    mouthOpenMinValue: -15,           // Composition.tsx:1281 (mode !== toast)
    mouthOpenMaxValue: -90,           // Composition.tsx:1280 (mode !== toast)
    censorInputName: "Censor",        // RiveRemotion.tsx:282
  },
  toast: {
    id: "toast",
    // Both characters are CONSOLIDATED into mr_roast.riv — toast loads the same
    // file but targets the toast-specific artboard (mrtoast / mrtoast_wide) +
    // SM_Main + MrToast view-model authored inside it. (mr_toast.riv is retired.)
    label: "Mr. Toast",
    riveSource: "/characters/mr_roast.riv",
    artboardName: "mrtoast",          // full-frame toast artboard (verified live)
    stateMachineName: "SM_Main",      // mrtoast uses SM_Main (SM_Toast = char-only artboards)
    viewModelName: "MrToast",
    emotionInputName: "Emotions",
    emotions: ["Idle", "Talking", "Surprised", "Point"] as const,
    mouthInputName: "Mouth",
    mouthClosedValue: 3,              // toast mouth: closed 3 (old web / iOS Mr Toast)
    mouthOpenMinValue: 8,             // smallest open value (unused by live lip-sync; closed→max is interpolated)
    mouthOpenMaxValue: 50,            // toast mouth: open 50 (old web / iOS Mr Toast)
    censorInputName: "Censor",
  },
} as const;

export const DEFAULT_CHARACTER: CharacterId = "roast";

export const getCharacter = (id: CharacterId): CharacterConfig => CHARACTERS[id];

/** Map a normalized audio amplitude [0,1] to this character's Mouth value. */
export function amplitudeToMouth(c: CharacterConfig, amp01: number): number {
  const a = Math.min(1, Math.max(0, amp01));
  return c.mouthClosedValue + (c.mouthOpenMaxValue - c.mouthClosedValue) * a;
}
